<script setup>
import { ref, watch, computed, onUnmounted, defineProps, defineEmits } from 'vue'
import {
  findTreeNodeKeyFromEventTarget,
  findTreeNodeByKey,
  isTreeNodeDescendant,
} from '@/composables/useHtmlEditorDom'

const props = defineProps({
  nodes: { type: Array, default: () => [] },
  selectedKey: { type: String, default: '' },
  expandedKeys: { type: Object, default: () => ({}) },
})

const emit = defineEmits(['select', 'update:expandedKeys', 'node-drop', 'context-action'])

const localExpandedKeys = ref({ ...props.expandedKeys })
const localNodes = ref([])
const treeContextMenuRef = ref(null)
const treeBodyRef = ref(null)
const contextNodeKey = ref('')
const dragState = ref(null)
const dropHint = ref(null)

const DRAG_THRESHOLD = 6
const DROP_EDGE_RATIO = 0.28

let pointerMoveHandler = null
let pointerUpHandler = null

watch(
  () => props.nodes,
  (val) => {
    localNodes.value = val
  },
  { deep: true, immediate: true }
)

const selectionKeys = computed({
  get: () => (props.selectedKey ? { [props.selectedKey]: true } : {}),
  set: (val) => {
    const key = Object.keys(val || {}).find((k) => val[k] === true || val[k]?.checked)
    if (key) emit('select', String(key))
  },
})

const treeContextMenuItems = computed(() => [
  {
    label: 'Дублировать',
    icon: 'pi pi-copy',
    command: () => {
      if (contextNodeKey.value) emit('context-action', { nodeId: contextNodeKey.value, action: 'duplicate' })
    },
  },
  {
    label: 'Удалить',
    icon: 'pi pi-trash',
    command: () => {
      if (contextNodeKey.value) emit('context-action', { nodeId: contextNodeKey.value, action: 'delete' })
    },
  },
])

watch(
  () => props.expandedKeys,
  (val) => {
    localExpandedKeys.value = { ...val }
  },
  { deep: true }
)

function onNodeSelect(event) {
  const key = event?.node?.key
  if (key) emit('select', String(key))
}

function onExpandedKeysChange(val) {
  localExpandedKeys.value = val
  emit('update:expandedKeys', val)
}

function isToggleTarget(target) {
  return Boolean(target?.closest?.('[data-pc-section="nodetogglebutton"]'))
}

function getTreeContentEl(key) {
  const root = treeBodyRef.value
  if (!root || !key) return null
  const label = root.querySelector(`[data-ncht-tree-key="${CSS.escape(key)}"]`)
  return label?.closest?.('.p-tree-node-content') || null
}

function clearDropHints() {
  const root = treeBodyRef.value
  if (!root) return
  root.querySelectorAll('.dom-tree-drop-before, .dom-tree-drop-after, .dom-tree-drop-into, .dom-tree-drag-source').forEach((el) => {
    el.classList.remove('dom-tree-drop-before', 'dom-tree-drop-after', 'dom-tree-drop-into', 'dom-tree-drag-source')
  })
}

function applyDropHint(key, position) {
  clearDropHints()
  const contentEl = getTreeContentEl(key)
  if (!contentEl) return
  if (position === 'before') contentEl.classList.add('dom-tree-drop-before')
  else if (position === 'after') contentEl.classList.add('dom-tree-drop-after')
  else contentEl.classList.add('dom-tree-drop-into')
}

function resolveDropHint(clientY, contentEl, dragKey, dropKey) {
  if (!contentEl || dragKey === dropKey) return null
  const dragNode = findTreeNodeByKey(props.nodes, dragKey)
  const dropNode = findTreeNodeByKey(props.nodes, dropKey)
  if (!dragNode || !dropNode) return null
  if (isTreeNodeDescendant(dragNode, dropKey)) return null

  const rect = contentEl.getBoundingClientRect()
  const rel = (clientY - rect.top) / Math.max(rect.height, 1)
  let position = 'into'
  if (rel < DROP_EDGE_RATIO) position = 'before'
  else if (rel > 1 - DROP_EDGE_RATIO) position = 'after'
  return { key: dropKey, position }
}

function dropPositionFromHint(position) {
  if (position === 'before') return -1
  if (position === 'after') return 1
  return 0
}

function detachPointerListeners() {
  if (pointerMoveHandler) {
    window.removeEventListener('pointermove', pointerMoveHandler)
    pointerMoveHandler = null
  }
  if (pointerUpHandler) {
    window.removeEventListener('pointerup', pointerUpHandler)
    window.removeEventListener('pointercancel', pointerUpHandler)
    pointerUpHandler = null
  }
}

function finishDrag() {
  detachPointerListeners()
  treeBodyRef.value?.classList.remove('dom-tree-is-dragging')
  clearDropHints()
  dragState.value = null
  dropHint.value = null
}

function onTreePointerMove(event) {
  const state = dragState.value
  if (!state) return

  const dx = event.clientX - state.startX
  const dy = event.clientY - state.startY

  if (!state.active) {
    if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return
    state.active = true
    treeBodyRef.value?.classList.add('dom-tree-is-dragging')
    const sourceEl = getTreeContentEl(state.key)
    sourceEl?.classList.add('dom-tree-drag-source')
  }

  const under = document.elementFromPoint(event.clientX, event.clientY)
  const dropKey = findTreeNodeKeyFromEventTarget(under)
  if (!dropKey) {
    dropHint.value = null
    clearDropHints()
    const sourceEl = getTreeContentEl(state.key)
    sourceEl?.classList.add('dom-tree-drag-source')
    return
  }

  const contentEl = getTreeContentEl(dropKey)
  const hint = resolveDropHint(event.clientY, contentEl, state.key, dropKey)
  dropHint.value = hint
  if (hint) applyDropHint(hint.key, hint.position)
  else {
    clearDropHints()
    getTreeContentEl(state.key)?.classList.add('dom-tree-drag-source')
  }
}

function onTreePointerUp(event) {
  const state = dragState.value
  const hint = dropHint.value
  finishDrag()

  if (!state?.active || !hint) return

  const dragNode = findTreeNodeByKey(props.nodes, state.key)
  const dropNode = findTreeNodeByKey(props.nodes, hint.key)
  if (!dragNode || !dropNode) return

  emit('node-drop', {
    originalEvent: event,
    dragNode,
    dropNode,
    dropPosition: dropPositionFromHint(hint.position),
    index: hint.position === 'before' ? 0 : 1,
  })
}

function onTreePointerDown(event) {
  if (event.button !== 0) return
  if (isToggleTarget(event.target)) return

  const key = findTreeNodeKeyFromEventTarget(event.target)
  if (!key) return

  finishDrag()
  dragState.value = { key, startX: event.clientX, startY: event.clientY, active: false }
  dropHint.value = null

  pointerMoveHandler = onTreePointerMove
  pointerUpHandler = onTreePointerUp
  window.addEventListener('pointermove', pointerMoveHandler)
  window.addEventListener('pointerup', pointerUpHandler)
  window.addEventListener('pointercancel', pointerUpHandler)
}

function onTreeContextMenu(event) {
  const key = findTreeNodeKeyFromEventTarget(event.target)
  if (!key) return
  contextNodeKey.value = key
  emit('select', key)
  event.preventDefault()
  treeContextMenuRef.value?.show?.(event)
}

onUnmounted(() => {
  finishDrag()
})
</script>

<template>
  <aside class="dom-tree-panel">
    <div class="dom-tree-title">Структура документа</div>
    <div
      v-if="nodes.length"
      ref="treeBodyRef"
      class="dom-tree-body"
      @pointerdown="onTreePointerDown"
      @contextmenu="onTreeContextMenu"
    >
      <TreeVue
        v-model:value="localNodes"
        selection-mode="single"
        v-model:selection-keys="selectionKeys"
        :expanded-keys="localExpandedKeys"
        class="dom-tree"
        @node-select="onNodeSelect"
        @update:expanded-keys="onExpandedKeysChange"
      >
        <template #default="{ node }">
          <span class="dom-tree-node-label" :data-ncht-tree-key="node.key">{{ node.label }}</span>
        </template>
      </TreeVue>
    </div>
    <div v-else class="dom-tree-empty">Дерево пустое</div>
    <ContextMenu ref="treeContextMenuRef" :model="treeContextMenuItems" />
  </aside>
</template>

<style scoped>
.dom-tree-panel {
  border: 1px solid var(--surface-border, #d1d5db);
  border-radius: 10px;
  background: var(--surface-card, #ffffff);
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.dom-tree-title {
  font-size: 13px;
  font-weight: 700;
  padding: 10px 10px 8px;
  color: var(--text-color, #111827);
  border-bottom: 1px solid var(--surface-border, #e5e7eb);
  flex-shrink: 0;
}

.dom-tree-body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 6px;
  touch-action: pan-y;
}

.dom-tree-body.dom-tree-is-dragging {
  user-select: none;
  cursor: grabbing;
}

.dom-tree-empty {
  padding: 12px;
  font-size: 13px;
  color: var(--text-color-secondary, #6b7280);
}

.dom-tree-node-label,
.dom-tree :deep(.p-tree-node-label) {
  font-size: 12px;
  line-height: 1.3;
  word-break: break-word;
}

.dom-tree :deep(.p-tree-node-content) {
  padding: 4px 6px;
  border-radius: 6px;
  cursor: grab;
}

.dom-tree :deep(.p-tree-node-content:active) {
  cursor: grabbing;
}

.dom-tree :deep(.p-tree-node-content.p-tree-node-selected) {
  background: rgba(34, 197, 94, 0.14);
  color: var(--text-color, #111827);
}

.dom-tree :deep(.p-tree-node-content.dom-tree-drag-source) {
  opacity: 0.45;
}

.dom-tree :deep(.p-tree-node-content.dom-tree-drop-before) {
  box-shadow: inset 0 2px 0 #22c55e;
}

.dom-tree :deep(.p-tree-node-content.dom-tree-drop-after) {
  box-shadow: inset 0 -2px 0 #22c55e;
}

.dom-tree :deep(.p-tree-node-content.dom-tree-drop-into) {
  background: rgba(34, 197, 94, 0.18);
  outline: 1px dashed #22c55e;
  outline-offset: -1px;
}

.dom-tree :deep(.p-tree-node-icon) {
  font-size: 11px;
  margin-right: 4px;
}
</style>
